import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { useAuth } from "@/context/AuthContext";
import { Crosshair } from "@/components/Crosshair";
import { timeAgo, BLOCK_REASONS } from "@/lib/constants";
import { toast } from "@/lib/toast";
import {
  Ban, ShieldAlert, RefreshCw, ShieldOff, AlertTriangle, X, Lock, Users, Shield, Loader2, Plus, Globe,
} from "lucide-react";
import { RoleTag } from "@/components/admin/RoleTag";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const REASON_LABEL = Object.fromEntries(BLOCK_REASONS.map((r) => [r.id, r.label]));
const STATUS_CLS = {
  active: "border-blaze/50 text-blaze",
  reversed: "border-teal-300/50 text-teal-300",
  removed: "border-sniper text-muted-foreground",
};
const TYPE_META = {
  member_block: { label: "Member", icon: Users, cls: "border-olive-bright/60 text-olive-bright" },
  platform_block: { label: "Platform", icon: Shield, cls: "border-[#7C8794]/70 text-[#B4BBC3]" },
};

function ReverseModal({ block, isSuper, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!block) return null;
  const type = block.block_type || "member_block";
  const isMember = type === "member_block";
  const tooShort = reason.trim().length < 20;
  const blockedByRule = isMember && !isSuper;

  const submit = async () => {
    if (tooShort) return;
    setBusy(true);
    try {
      await api.post(`/admin/blocks/${block.block_id}/reverse`, { reason: reason.trim() });
      toast.success("Block reversed. The member who placed it has been notified.");
      onDone();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-surface border-sniper text-foreground max-w-lg" data-testid="reverse-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
            <ShieldOff className="w-5 h-5 text-teal-300" /> Reverse this block
          </DialogTitle>
          <DialogDescription className="font-body text-secondary-foreground">
            @{block.blocker_username} blocked @{block.blocked_username} · reason: {REASON_LABEL[block.reason] || block.reason}
            {block.note ? ` — “${block.note}”` : ""} · placed {timeAgo(block.created_at)}
          </DialogDescription>
        </DialogHeader>

        {isMember && (
          <div className="border border-blaze/50 bg-blaze/10 p-3 flex gap-2" data-testid="reverse-member-warning">
            <AlertTriangle className="w-4 h-4 text-blaze shrink-0 mt-0.5" />
            <p className="text-[13px] font-body text-secondary-foreground leading-snug">
              This is a <strong className="text-foreground">member safety block</strong>. Reversing it forces
              @{block.blocker_username} back into contact with someone they deliberately cut off. They will be
              emailed that you did this, and why. This action requires <strong className="text-foreground">super admin</strong>.
            </p>
          </div>
        )}

        {blockedByRule ? (
          <div className="border border-sniper bg-obsidian p-4 flex items-center gap-2 font-mono text-sm text-muted-foreground" data-testid="reverse-needs-super">
            <Lock className="w-4 h-4" /> Reversing a member block requires super admin.
          </div>
        ) : (
          <div>
            <label className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">
              Reason for reversal (required · min 20 chars)
            </label>
            <textarea
              data-testid="reverse-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              autoFocus
              placeholder="Explain why this block is being reversed. This is recorded in the audit log."
              className="mt-1 w-full bg-input border border-sniper focus:border-teal-300 outline-none px-3 py-2.5 text-foreground text-sm resize-none"
            />
            <p className={`mt-1 font-mono text-[11px] ${tooShort ? "text-blaze" : "text-muted-foreground"}`} data-testid="reverse-reason-count">
              {reason.trim().length}/20 characters minimum
            </p>
          </div>
        )}

        <DialogFooter>
          <button onClick={onClose} data-testid="reverse-cancel"
            className="px-4 py-2 border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-xs transition-colors">
            Cancel
          </button>
          {!blockedByRule && (
            <button onClick={submit} disabled={busy || tooShort} data-testid="reverse-confirm"
              className="px-4 py-2 bg-teal-600 hover:bg-teal-500 disabled:opacity-40 text-white font-heading font-bold uppercase text-xs transition-colors inline-flex items-center gap-1.5">
              <ShieldOff className="w-3.5 h-3.5" /> {busy ? "Reversing…" : "Reverse block"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IpBlocksPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ip: "", reason: "", note: "", banUsername: "" });
  const [busy, setBusy] = useState(false);
  const [selfConfirm, setSelfConfirm] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/ip-blocks").then(({ data }) => setRows(data.ip_blocks || []))
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (confirmSelf = false) => {
    if (!form.ip.trim()) { toast.error("Enter an IP address."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ip-blocks", {
        ip: form.ip.trim(), reason: form.reason.trim(), note: form.note.trim(),
        ban_username: form.banUsername.trim() || undefined, confirm_self: confirmSelf,
      });
      toast.success(`IP ${data.ip} blocked${data.banned_username ? ` · @${data.banned_username} banned` : ""}.`);
      setForm({ ip: "", reason: "", note: "", banUsername: "" }); setSelfConfirm(false); load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "self_ip") { setSelfConfirm(true); toast.error(detail.message); }
      else toast.error(formatError(detail) || "Couldn't block that IP.");
    } finally { setBusy(false); }
  };

  const unblock = async (b) => {
    try { await api.delete(`/admin/ip-blocks/${b.block_id}`); toast.success(`IP ${b.ip} unblocked.`); load(); }
    catch (err) { toast.error(formatError(err?.response?.data?.detail)); }
  };

  const inputCls = "w-full bg-input border border-sniper focus:border-blaze outline-none px-3 py-2 text-foreground font-body text-sm transition-colors";

  return (
    <div className="mt-10" data-testid="admin-ip-blocks">
      <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2 mb-1">
        <Globe className="w-4 h-4 text-blaze" /> IP Address Blocks
      </h3>
      <p className="text-sm font-body text-secondary-foreground mb-3">
        A blocked IP is rejected (HTTP 403) on <strong className="text-foreground">every</strong> request, at the app level.
        Blocking an IP and banning an account are independent — use the field below to also ban an account in the same action.
      </p>

      {/* Block an IP form */}
      <div className="border border-sniper bg-surface p-4 mb-4" data-testid="ip-block-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.ip} onChange={set("ip")} data-testid="ip-block-ip"
            placeholder="IP address (e.g. 75.98.185.171)" className={inputCls} />
          <input value={form.banUsername} onChange={set("banUsername")} data-testid="ip-block-ban-username"
            placeholder="Also ban account @username (optional)" className={inputCls} />
          <input value={form.reason} onChange={set("reason")} data-testid="ip-block-reason"
            placeholder="Reason (optional, logged)" className={inputCls} />
          <input value={form.note} onChange={set("note")} data-testid="ip-block-note"
            placeholder="Note (optional)" className={inputCls} />
        </div>
        {selfConfirm && (
          <div className="mt-3 border border-amber-400/50 bg-amber-400/10 p-3 flex gap-2" data-testid="ip-block-self-warning">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="font-body text-[13px] text-amber-200 leading-snug">
              That's your current IP — blocking it will lock you out from this network. Click again to confirm.
            </p>
          </div>
        )}
        <button onClick={() => submit(selfConfirm)} disabled={busy} data-testid="ip-block-submit"
          className="mt-3 inline-flex items-center gap-1.5 bg-blaze hover:bg-blaze/80 disabled:opacity-40 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          {selfConfirm ? "Confirm block my IP" : "Block IP"}
        </button>
      </div>

      {/* Blocked IP list */}
      <div className="border border-sniper bg-surface" data-testid="ip-block-list">
        {loading ? (
          <p className="p-6 text-center font-mono uppercase text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-center font-mono uppercase text-sm text-muted-foreground" data-testid="ip-block-empty">No IP blocks yet.</p>
        ) : (
          <ul className="divide-y divide-sniper">
            {rows.map((b) => (
              <li key={b.block_id} className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`ip-block-row-${b.block_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-foreground">{b.ip}</span>
                    <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${b.status === "active" ? "border-blaze/50 text-blaze" : "border-sniper text-muted-foreground"}`}>{b.status}</span>
                    {b.banned_username && (
                      <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 border border-amber-400/50 text-amber-400">banned @{b.banned_username}</span>
                    )}
                  </div>
                  <p className="text-[11px] font-mono uppercase text-muted-foreground mt-0.5">
                    {b.reason || "no reason"}{b.note ? ` — ${b.note}` : ""} · by @{b.blocked_by_username || "admin"} · {timeAgo(b.created_at)}
                  </p>
                </div>
                {b.status === "active" && (
                  <button onClick={() => unblock(b)} data-testid={`ip-unblock-${b.block_id}`}
                    className="shrink-0 inline-flex items-center gap-1 border border-teal-300/50 text-teal-300 hover:bg-teal-300/10 font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                    <ShieldOff className="w-3 h-3" /> Unblock
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


export default function AdminBlocks() {
  const { user } = useAuth();
  const isSuper = user?.role === "admin" || user?.role === "super_admin"; // admin == super privileges
  const [data, setData] = useState({ blocks: [], flagged: [], threshold: 2, counts: { active: 0, reversed: 0, all: 0 } });
  const [tab, setTab] = useState("active");
  const [modal, setModal] = useState(null);
  const [loadErr, setLoadErr] = useState(null);

  const load = (t = tab) => {
    const q = t === "all" ? "" : `?status=${t}`;
    setLoadErr(null);
    api.get(`/admin/blocks${q}`).then(({ data }) => setData(data))
      .catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load blocks."));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(tab); }, [tab]);

  const flaggedOverThreshold = (data.flagged || []).filter((f) => f.count >= data.threshold);
  const counts = data.counts || { active: 0, reversed: 0, all: 0 };
  const TABS = [["active", `Active (${counts.active})`], ["reversed", `Reversed (${counts.reversed})`], ["all", `All (${counts.all})`]];

  return (
    <div className="text-foreground" data-testid="admin-blocks">
      <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2">
        <Ban className="w-7 h-7 text-blaze" /> Blocks
      </h2>
      <p className="text-sm font-body text-secondary-foreground mt-2">
        <strong className="text-foreground">Member blocks</strong> are a safety control (one member cutting off
        another) and live here. <strong className="text-foreground">Platform blocks</strong> are moderation
        enforcement (bans / account holds) and live in Moderation &amp; Accounts. Reversing a member block needs
        super admin, a written reason, and notifies the member who placed it.
      </p>
      <Crosshair className="my-5" />

      {flaggedOverThreshold.length > 0 && (
        <div className="mb-6" data-testid="blocks-flagged">
          <h3 className="font-heading text-lg uppercase tracking-tight text-blaze flex items-center gap-2 mb-2">
            <ShieldAlert className="w-4 h-4" /> Flagged accounts ({flaggedOverThreshold.length})
          </h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {flaggedOverThreshold.map((f) => (
              <div key={f.blocked_id} className="border border-blaze/40 bg-blaze/5 px-4 py-3 flex items-center justify-between" data-testid={`flagged-${f.blocked_id}`}>
                <Link to={`/u/${f.blocked_username}`} className="font-mono text-sm text-foreground hover:text-blaze">@{f.blocked_username}</Link> <RoleTag kind={f.blocked_kind} />
                <span className="font-mono text-[11px] uppercase text-blaze flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> {f.count} distinct blocks
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* filter tabs */}
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex border border-sniper" data-testid="blocks-tabs">
          {TABS.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`blocks-tab-${id}`}
              className={`px-4 py-2 font-mono uppercase text-[11px] tracking-wide transition-colors ${tab === id ? "bg-olive text-white" : "text-secondary-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
        <button onClick={() => load()} className="p-2 border border-sniper text-muted-foreground hover:text-foreground transition-colors" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="border border-sniper bg-surface" data-testid="blocks-list">
        {loadErr ? (
          <LoadFailed reason={loadErr} onRetry={() => load()} testid="blocks-load-failed" />
        ) : data.blocks.length === 0 ? (
          <p className="p-8 text-center font-mono uppercase text-sm text-muted-foreground">No blocks in this view.</p>
        ) : (
          <ul className="divide-y divide-sniper">
            {data.blocks.map((b) => {
              const tm = TYPE_META[b.block_type || "member_block"];
              const TIcon = tm.icon;
              return (
                <li key={b.block_id} className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`block-row-${b.block_id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 font-mono text-[9px] uppercase px-1.5 py-0.5 border ${tm.cls}`} data-testid={`block-type-${b.block_id}`}>
                        <TIcon className="w-2.5 h-2.5" /> {tm.label}
                      </span>
                      <p className="text-sm font-body text-foreground">
                        <Link to={`/u/${b.blocker_username}`} className="text-secondary-foreground hover:text-foreground">@{b.blocker_username}</Link> <RoleTag kind={b.blocker_kind} />
                        <span className="text-muted-foreground"> blocked </span>
                        <Link to={`/u/${b.blocked_username}`} className="text-secondary-foreground hover:text-foreground">@{b.blocked_username}</Link> <RoleTag kind={b.blocked_kind} />
                      </p>
                    </div>
                    <p className="text-[11px] font-mono uppercase text-muted-foreground mt-0.5">
                      {REASON_LABEL[b.reason] || b.reason}{b.note ? ` — ${b.note}` : ""} · {timeAgo(b.created_at)}
                    </p>
                    {b.status === "reversed" && (
                      <div className="mt-1 border-l-2 border-teal-300/50 pl-2" data-testid={`reversal-info-${b.block_id}`}>
                        <p className="text-[11px] font-mono text-teal-300">
                          Reversed by @{b.reversed_by_username || "admin"} · {timeAgo(b.reversed_at)}
                          {b.reversal_active_human ? ` · was active ${b.reversal_active_human}` : ""}
                        </p>
                        {b.reverse_reason && <p className="text-[11px] font-body text-secondary-foreground mt-0.5">“{b.reverse_reason}”</p>}
                      </div>
                    )}
                    {b.status === "active" && b.reversal_locked && (
                      <p className="text-[11px] font-mono text-blaze mt-1 flex items-center gap-1" data-testid={`reblock-locked-${b.block_id}`}>
                        <Lock className="w-3 h-3" /> Re-blocked after an admin reversal — escalate to owner
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${STATUS_CLS[b.status] || "border-sniper text-muted-foreground"}`}>{b.status}</span>
                    {b.status === "active" && !b.reversal_locked && (
                      <button onClick={() => setModal(b)} data-testid={`reverse-${b.block_id}`}
                        className="inline-flex items-center gap-1 border border-teal-300/50 text-teal-300 hover:bg-teal-300/10 font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                        <ShieldOff className="w-3 h-3" /> Reverse
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <IpBlocksPanel />

      {modal && (
        <ReverseModal
          block={modal}
          isSuper={isSuper}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
